import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { LedgerEntry, Overview, TariffTier, operatorApi, priceForQuantity, useFetch } from '../../services/operatorApi';
import { Badge, Button, Card, Empty, ErrorBox, Field, PageHeader, Spinner, StatCard, Table, Td, dateTime, eur, inputClass, num } from '../../components/ui';

const PAGE = 25;

interface OrderRow {
  id: string;
  status: 'pending' | 'paid' | 'expired' | 'failed';
  email: string;
  name: string;
  company: string | null;
  quantity: number;
  amount_ht_cents: number;
  vat_cents: number | null;
  total_cents: number | null;
  organization_id: string | null;
  client: string | null;
  new_account: boolean | null;
  email_sent: boolean | null;
  invoice_url: string | null;
  created_at: string;
}

const ORDER_STATUS: Record<OrderRow['status'], [string, 'green' | 'amber' | 'slate' | 'red']> = {
  paid: ['Payée', 'green'],
  pending: ['En attente', 'amber'],
  expired: ['Abandonnée', 'slate'],
  failed: ['Échec', 'red'],
};
const KINDS: Record<string, [string, 'green' | 'blue' | 'slate' | 'amber']> = {
  purchase: ['Achat de lot', 'green'],
  grant: ['Offert', 'blue'],
  consumption: ['Signature', 'slate'],
  adjustment: ['Correction', 'amber'],
};

export default function CreditsPage() {
  const overview = useFetch(async () => (await operatorApi.get<Overview>('/overview', { params: { days: 30 } })).data);
  const settings = useFetch(async () => (await operatorApi.get<{ tariff: { tiers: TariffTier[] } }>('/settings')).data);
  const orders = useFetch(async () => (await operatorApi.get<{ orders: OrderRow[] }>('/orders')).data.orders);
  const [kind, setKind] = useState('');
  const [offset, setOffset] = useState(0);
  const [quantity, setQuantity] = useState('100');
  const ledger = useFetch(
    async () => (await operatorApi.get<{ entries: LedgerEntry[]; total: number }>('/ledger', { params: { kind: kind || undefined, limit: PAGE, offset } })).data,
    [kind, offset]
  );

  const tiers = settings.data?.tariff.tiers ?? [];
  const qty = Math.max(0, Math.floor(Number(quantity) || 0));
  const price = priceForQuantity(tiers, qty);
  const money = overview.data?.money;
  const total = ledger.data?.total ?? 0;

  return (
    <div>
      <PageHeader title="Ventes et crédits" subtitle="Lots vendus, signatures consommées et grille tarifaire" />

      {money ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Ventes cumulées" value={eur(money.sales_total_cents)} hint={`${num(money.credits_sold)} signatures vendues`} />
          <StatCard label="Prix moyen" value={eur(money.average_price_cents)} hint="par signature vendue" />
          <StatCard label="Consommées" value={num(money.credits_consumed_total)} hint={`${num(money.credits_granted)} offertes au total`} />
          <StatCard label="En circulation" value={num(money.credits_outstanding)} hint="signatures prépayées non encore utilisées" />
        </div>
      ) : overview.loading ? (
        <Spinner />
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title="Calculateur de lot">
          <Field label="Nombre de signatures">
            <input className={inputClass} type="number" min={0} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </Field>
          <div className="mt-4 rounded-lg bg-slate-50 p-4">
            <div className="text-3xl font-semibold tabular-nums text-slate-900">{eur(price)}</div>
            <div className="mt-1 text-sm text-slate-500">{qty > 0 ? `soit ${eur(price / qty)} par signature` : 'Saisissez une quantité'}</div>
          </div>
          <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
            {tiers.map((tier, index) => {
              const from = index === 0 ? 1 : (tiers[index - 1].up_to ?? 0) + 1;
              return (
                <li key={index} className="flex justify-between">
                  <span>{tier.up_to === null ? `À partir de ${from}` : `De ${from} à ${tier.up_to}`}</span>
                  <span className="tabular-nums">{eur(tier.unit_cents)} / signature</span>
                </li>
              );
            })}
          </ul>
          <p className="mt-4 text-xs text-slate-500">
            Tarif au volume : tout le lot est facturé au prix de sa tranche. Modifiable dans <Link to="/settings" className="text-indigo-600 hover:underline">Réglages</Link>. Pour vendre un lot, ouvrez la fiche du client puis « Créditer ».
          </p>
        </Card>

        <Card title="Journal des crédits" className="xl:col-span-2">
          <div className="mb-4">
            <select className={`${inputClass} !w-auto`} value={kind} onChange={(e) => { setKind(e.target.value); setOffset(0); }}>
              <option value="">Tous les mouvements</option>
              <option value="purchase">Achats de lots</option>
              <option value="grant">Signatures offertes</option>
              <option value="consumption">Consommations</option>
              <option value="adjustment">Corrections</option>
            </select>
          </div>
          {ledger.loading && !ledger.data ? (
            <Spinner />
          ) : ledger.error ? (
            <ErrorBox message={ledger.error} onRetry={ledger.reload} />
          ) : !ledger.data || ledger.data.entries.length === 0 ? (
            <Empty>Aucun mouvement.</Empty>
          ) : (
            <>
              <Table head={['Date', 'Client', 'Type', 'Crédits', 'Montant', 'Référence']}>
                {ledger.data.entries.map((e) => (
                  <tr key={e.id}>
                    <Td className="whitespace-nowrap">{dateTime(e.created_at)}</Td>
                    <Td><Link className="hover:text-indigo-600" to={`/clients/${e.client_id}`}>{e.client}</Link></Td>
                    <Td><Badge tone={KINDS[e.kind][1]}>{KINDS[e.kind][0]}</Badge></Td>
                    <Td className={`tabular-nums font-medium ${e.credits < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{e.credits > 0 ? `+${e.credits}` : e.credits}</Td>
                    <Td className="tabular-nums">{e.amount_cents === null ? '—' : eur(e.amount_cents)}</Td>
                    <Td className="text-slate-500">{[e.reference, e.note].filter(Boolean).join(' · ') || '—'}</Td>
                  </tr>
                ))}
              </Table>
              <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                <span>{num(offset + 1)}–{num(Math.min(offset + PAGE, total))} sur {num(total)}</span>
                <div className="flex gap-2">
                  <Button variant="secondary" small disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="secondary" small disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      <Card title="Commandes en ligne" className="mt-6">
        {orders.loading && !orders.data ? (
          <Spinner />
        ) : orders.error ? (
          <ErrorBox message={orders.error} onRetry={orders.reload} />
        ) : !orders.data || orders.data.length === 0 ? (
          <Empty>Aucune commande en ligne pour le moment.</Empty>
        ) : (
          <Table head={['Date', 'Acheteur', 'Lot', 'HT', 'TTC', 'Statut', 'Accès', 'Facture']}>
            {orders.data.map((o) => (
              <tr key={o.id}>
                <Td className="whitespace-nowrap">{dateTime(o.created_at)}</Td>
                <Td>
                  {o.organization_id ? <Link className="font-medium hover:text-indigo-600" to={`/clients/${o.organization_id}`}>{o.client ?? o.name}</Link> : <span className="font-medium">{o.company || o.name}</span>}
                  <div className="text-xs text-slate-500">{o.email}</div>
                </Td>
                <Td className="tabular-nums">{num(o.quantity)}</Td>
                <Td className="tabular-nums">{eur(o.amount_ht_cents)}</Td>
                <Td className="tabular-nums">{o.total_cents === null ? '—' : eur(o.total_cents)}</Td>
                <Td><Badge tone={ORDER_STATUS[o.status][1]}>{ORDER_STATUS[o.status][0]}</Badge></Td>
                <Td>
                  {o.status !== 'paid' ? '—' : o.email_sent ? <Badge tone="green">{o.new_account ? 'Clé envoyée' : 'Lot ajouté'}</Badge> : <Badge tone="red">E-mail non parti</Badge>}
                </Td>
                <Td>{o.invoice_url ? <a className="text-indigo-600 hover:underline" href={o.invoice_url} target="_blank" rel="noreferrer">Voir</a> : '—'}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
